/**
 * composer-v2/lib/mentionTransformer — Lexical `TextMatchTransformer` that
 * round-trips a `<BeautifulMentionNode>` through the canonical ADR-0044
 * sentinel `@[Display Name](user:uuid)`. KAP-303.
 *
 * **Why this is the most load-bearing piece of KAP-303:**
 * `@lexical/markdown` ships no mention transformer. Without this file, a
 * BeautifulMentionNode round-trip through `$convertToMarkdownString` falls
 * back to its `getTextContent()` (display name only), silently losing the
 * user id. Reloading the message turns the rich chip into bare text — the
 * data-corruption failure mode flagged in KAP-303's "riskiest sub-issue"
 * warning.
 *
 * Append to `COMPOSER_TRANSFORMERS` after the standard `TRANSFORMERS` set
 * in `MessageComposerV2.js`. The renderer-side parser lives in
 * `frontend/src/components/chat/MarkdownRenderer.js` and consumes the
 * SAME `MENTION_SENTINEL_REGEX` from `frontend/src/lib/messageMarkdown.js`
 * per ADR-0044's "shared parser module" requirement.
 */

import { $createTextNode } from 'lexical';
import { $getNearestNodeOfType } from '@lexical/utils';
import { CodeNode } from '@lexical/code';
import { SentinelMentionNode } from './SentinelMentionNode';
import { MENTION_SENTINEL_REGEX } from '../../../../lib/messageMarkdown';

// $-anchored copy of the canonical regex for the live-typing path. The
// `regExp` field on a TextMatchTransformer must match at end-of-text;
// `MENTION_SENTINEL_REGEX` is a /g global without anchors, which the
// transformer framework rejects.
const MENTION_SENTINEL_LIVE_REGEX = new RegExp(
  `@\\[((?:\\\\\\]|[^\\]])+)\\]\\(user:([0-9a-fA-F-]{36})\\)$`
);

function unescapeLabel(label) {
  return String(label).replace(/\\\]/g, ']');
}

export function createMentionTransformer() {
  return {
    // IMPORT-ONLY transformer. The serialization side (export) is handled
    // at the node level by SentinelMentionNode.getTextContent(), not by
    // the @lexical/markdown transformer pass — `$convertToMarkdownString`
    // doesn't reliably invoke text-match export on DecoratorNode
    // instances (confirmed via live smoke against dev). Returning null
    // here is a no-op for the serialize path; getTextContent owns it.
    dependencies: [SentinelMentionNode],
    export: () => null,
    importRegExp: new RegExp(MENTION_SENTINEL_REGEX.source, 'i'),
    regExp: MENTION_SENTINEL_LIVE_REGEX,
    replace: (textNode, match) => {
      const rawDisplay = match && match[1];
      const userId = match && match[2];
      if (typeof rawDisplay !== 'string' || typeof userId !== 'string') return;
      // ADR-0044 §2 — a sentinel inside a fenced code block MUST stay
      // literal. The `EnterToSendPlugin` defers to ListPlugin inside lists
      // and CodeNode for the same reason; this is the same guardrail.
      if ($getNearestNodeOfType(textNode, CodeNode)) {
        textNode.replace($createTextNode(textNode.getTextContent()));
        return;
      }
      const displayName = unescapeLabel(rawDisplay);
      const node = new SentinelMentionNode('@', displayName, {
        id: userId,
        username: userId,
        displayName
      });
      textNode.replace(node);
    },
    // Closing paren of the URL portion — same shape as createEmojiShortcodeTransformer
    // (which uses `:`) so the framework knows when to attempt a match.
    trigger: ')',
    type: 'text-match'
  };
}
